/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { CategoryBreadcrumbsSkeleton } from '@/components/category-breadcrumbs/skeleton';
import { ProductTileSkeleton } from '@/components/category-skeleton';
import { Skeleton } from '@/components/ui/skeleton';
import { SeoMeta } from '@/components/seo-meta';
import { readClientGridPreference } from '@/hooks/use-grid-columns';

const DEFAULT_PRODUCT_COUNT = 24;

/**
 * Data-independent PLP shell shown from the current layout as soon as a category
 * navigation starts. It deliberately contains no category text: the target
 * category is not trusted until the blocking category lookup has completed.
 */
const MOBILE_GRID_COLS = { 1: 'grid-cols-1', 2: 'grid-cols-2' } as const;
const DESKTOP_GRID_COLS = { 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4' } as const;

export default function CategoryPendingShell({ productCount = DEFAULT_PRODUCT_COUNT }: { productCount?: number }) {
    const columns = readClientGridPreference();
    const colClass = `${MOBILE_GRID_COLS[columns.mobile]} ${DESKTOP_GRID_COLS[columns.desktop]}`;

    return (
        <>
            <SeoMeta />
            <div className="pb-16 -mt-8" aria-busy="true" data-testid="category-navigation-pending-shell">
                <div className="px-8 pt-8 pb-4">
                    <CategoryBreadcrumbsSkeleton />
                    <div className="flex items-center gap-2" aria-hidden="true">
                        <Skeleton className="h-8 w-64" />
                        <Skeleton className="h-7 w-12" />
                    </div>
                </div>
                <div className="h-16 border-y border-border px-4 md:px-8 flex items-center justify-between gap-4">
                    <Skeleton className="h-8 w-20" />
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-8 w-28" />
                </div>
                <div className="px-4 pt-8 md:px-8">
                    <div className={`grid ${colClass} gap-x-4 gap-y-8`}>
                        {Array.from({ length: productCount }, (_, index) => (
                            <ProductTileSkeleton key={index} />
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
}
